<script lang="ts">
  import {
    checkmarkCircleCheckedSolidIcon,
    xmarkCircleCheckedSolidIcon,
    xmarkIcon,
  } from '@nrk/core-icons';
  import { fly } from 'svelte/transition';
  import Icon from '../lib/Icon.svelte';
  import { removeToast, toasts } from '../stores/toastStore';

  let container = $state<HTMLElement>();

  /* Everything outside an open modal dialog is inert (the dialog also marks the
     document inert), so toasts rendered at the page level would be unclickable
     and clicks would hit the dialog backdrop instead. Descendants of the modal
     stay interactive, so the container moves into the topmost open modal and
     back to <body> when it closes. */
  const placeContainer = (el: HTMLElement) => {
    const modal = [...document.querySelectorAll<HTMLElement>('dialog[open]')]
      .filter((d) => d.matches(':modal'))
      .at(-1);
    const host = modal ?? document.body;
    if (el.parentElement === host && el.isConnected) return;
    host.append(el);
    /* Moving a node resets its popover state. */
    if ($toasts.length > 0) liftToTopLayer(el);
  };

  /* Modal dialogs live in the top layer, above any z-index, and the top layer
     stacks in insertion order. A manual popover joins it, and re-showing it
     lifts it above whichever dialog was opened last. */
  const liftToTopLayer = (el: HTMLElement) => {
    if (el.matches(':popover-open')) el.hidePopover();
    el.showPopover();
  };

  $effect(() => {
    if (!container) return;
    const el = container;
    const observer = new MutationObserver(() => placeContainer(el));
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open'],
    });
    return () => observer.disconnect();
  });

  $effect(() => {
    if (!container || $toasts.length === 0) return;
    placeContainer(container);
    liftToTopLayer(container);
  });
</script>

<!-- Errors interrupt (alert), successes wait their turn (status). -->
<section bind:this={container} popover="manual" aria-label="Varsler">
  {#each $toasts as { id, type, message } (id)}
    <div
      class={['toast', type]}
      role={type === 'error' ? 'alert' : 'status'}
      transition:fly={{ y: -12, duration: 150 }}
    >
      <span class="status-icon">
        <Icon
          svg={type === 'error'
            ? xmarkCircleCheckedSolidIcon
            : checkmarkCircleCheckedSolidIcon}
        />
      </span>
      <p class="message">{message}</p>
      <button
        class="dismiss"
        type="button"
        onclick={() => removeToast(id)}
        aria-label="Lukk varsel"
      >
        <Icon svg={xmarkIcon} />
      </button>
    </div>
  {/each}
</section>

<style>
  /* Reset the popover UA styles (centered box, border, background). */
  section {
    position: fixed;
    inset: var(--space-4) var(--space-4) auto auto;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    overflow: visible;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-2);
    max-inline-size: calc(100vw - 2 * var(--space-4));
    /* The empty gaps must not block clicks on the page or dialog behind. */
    pointer-events: none;
  }

  section:not(:popover-open) {
    display: none;
  }

  .toast {
    --toast-accent: var(--success);

    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: start;
    gap: var(--space-3);
    inline-size: 22rem;
    max-inline-size: 100%;
    padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
    background: var(--surface-raised);
    color: var(--text);
    border: var(--border-width) solid var(--border);
    border-inline-start: var(--space-1) solid var(--toast-accent);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    pointer-events: auto;

    &.error {
      --toast-accent: var(--danger);
    }
  }

  .status-icon {
    display: flex;
    color: var(--toast-accent);
    font-size: var(--text-xl);
  }

  .message {
    margin: 0;
    padding-block: var(--space-1);
    font-size: var(--text-sm);
    overflow-wrap: anywhere;
  }

  .dismiss {
    display: flex;
    padding: var(--space-1);
    background: none;
    border: none;
    border-radius: var(--radius-md);
    color: var(--text-muted);
    cursor: pointer;

    &:hover {
      background: var(--surface-hover);
      color: var(--text);
    }
  }
</style>
